import { useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type Ref } from "react";
import { fold, type PanelKind } from "../panels.ts";
import { locate, placeBridges, revealIn, scrollerOf, scrollToShow } from "./Bridge.ts";
import { Icon } from "./Icons.tsx";

// The docked row (docs/design 4.3): sidebar | board | panel | panel ..., left-aligned with a 20px
// gutter the bridges cross. The board keeps 1080px at most (never centred while a panel is open)
// and shrinks to 360 before anything folds; panels are 380 to 560; older panels fold into 36px
// spines when the row doesn't fit, and past that the row scrolls sideways with the newest in view.
// Under 640px the panels are full-screen sheets over the board instead, and there is no bridge.

export interface ColumnPanel {
  /** the panel's id (stable while it is open) */
  id: number;
  kind: PanelKind;
  /** for screen readers: "Project 03 Delivery app", "Item 03-01" */
  label: string;
  /** what it says folded into a spine, turned on its side: "03 Delivery app", "03-01" */
  spine: string;
  pinned: boolean;
  /** what it was opened from (a card or a row in `column`); a bridge is drawn when that is the column left of it */
  /** `part: "notes"`: a notes panel, tied to the notes row on that card rather than the whole card */
  source: { column: number; num: number; via?: number; label: string; part?: "notes" } | null;
  content: ReactNode;
}

/** Keep a source where it was on screen after a panel opens (narrowing the board reflows it), and in view. */
export type Reveal = { column: number; num: number; via?: number; top: number | null; part?: "notes" } | { all: true };

/** The first card in view in the board column (under its sticky header), and where it is on screen. */
function firstCardInView(board: HTMLElement): { num: string; top: number } | null {
  const head = board.querySelector(":scope > .c1-stick, :scope > .onerow-wrap");
  const vTop = Math.max(0, head?.getBoundingClientRect().bottom ?? 0);
  for (const slot of board.querySelectorAll<HTMLElement>("[data-card]")) {
    const r = slot.previousElementSibling?.getBoundingClientRect();
    if (r && r.bottom > vTop + 4) return { num: slot.dataset.card ?? "", top: r.top };
  }
  return null;
}

/** What the person does that means "I'm reading now": settling stops at the first of these. */
const TAKE_OVER = ["wheel", "touchstart", "pointerdown", "keydown"] as const;

/**
 * Run `again` whenever what's in the columns changes size during the next `ms`, until the person
 * scrolls, taps, clicks or types in the row (then their position wins).
 */
function settle(row: HTMLElement, again: () => void, ms = 1500) {
  const ro = new ResizeObserver(() => again());
  row.querySelectorAll(":scope > .col > *, :scope > .pcol > .pscroll > *").forEach((n) => ro.observe(n));
  const stop = () => {
    ro.disconnect();
    clearTimeout(timer);
    for (const e of TAKE_OVER) row.removeEventListener(e, stop, true);
  };
  for (const e of TAKE_OVER) row.addEventListener(e, stop, { capture: true, passive: true });
  const timer = setTimeout(stop, ms);
}

export interface ColumnsHandle {
  /** bring panel `index`'s source into view (unfolding its column if it is a spine) and flash it */
  showSource: (index: number) => void;
}

export function Columns({
  board,
  panels,
  expanded,
  phone,
  onExpand,
  onMissingSource,
  parentOf,
  reveal,
  handle,
}: {
  board: ReactNode;
  panels: ColumnPanel[];
  /** the panel expanded by hand from a spine */
  expanded: number | null;
  phone: boolean;
  onExpand: (id: number | null) => void;
  /** the source isn't drawn in its column (filtered out, say): let that column find it */
  onMissingSource: (column: number, num: number) => void;
  /** an item's parent, to find the card a sub-item's row is on */
  parentOf: (num: number) => number | null;
  reveal: { current: Reveal | null };
  handle?: Ref<ColumnsHandle>;
}) {
  const row = useRef<HTMLDivElement>(null);
  const docked = !phone && panels.length > 0;
  const [avail, setAvail] = useState(0);
  const ids = panels.map((p) => p.id);
  const idKey = ids.join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const folded = useMemo(() => (docked ? fold(avail, ids, expanded) : new Set<number>()), [docked, avail, idKey, expanded]);
  const foldKey = [...folded].join(",");

  // the room the row has (the sidebar shrinks to a rail with two panels, so measure after each change)
  useLayoutEffect(() => {
    const measure = () => row.current && setAvail(row.current.clientWidth);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [idKey, docked]);

  // the page scrolls while no panel is docked; the board column scrolls by itself while one is.
  // Carry the scroll position across, so opening the first panel doesn't move the board.
  // (Read while rendering the switch, before it reaches the page: once it has, the old scroller has
  // lost its position.)
  // The board reflows to its new width, so the first card in view is kept where it is on screen.
  const pageY = useRef(0);
  const boardY = useRef(0);
  const anchor = useRef<{ num: string; top: number } | null>(null);
  const wasDocked = useRef(docked);
  const captured = useRef<boolean | null>(null);
  if (docked !== wasDocked.current && row.current && captured.current !== docked) {
    captured.current = docked;
    const board0 = scrollerOf(row.current, 0);
    if (wasDocked.current) boardY.current = board0?.scrollTop ?? 0;
    else pageY.current = window.scrollY;
    anchor.current = board0 ? firstCardInView(board0) : null;
  }

  // all the drawing reads the latest props through this
  const state = useRef({ panels, folded, phone, parentOf });
  state.current = { panels, folded, phone, parentOf };
  const raf = useRef(0);
  const schedule = useRef(() => {});
  schedule.current = () => {
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => row.current && placeBridges(row.current, state.current));
  };

  useLayoutEffect(() => {
    const el = row.current;
    // the room was just measured anew: the folds are about to change, so wait for that render
    if (!el || (docked && el.clientWidth !== avail)) return;
    const board0 = scrollerOf(el, 0);
    if (docked !== wasDocked.current) {
      if (docked && board0) board0.scrollTop = pageY.current;
      else if (!docked) window.scrollTo({ top: boardY.current });
      const a = anchor.current;
      const card = a && board0?.querySelector(`[data-card="${a.num}"]`)?.previousElementSibling;
      if (a && card) {
        const delta = card.getBoundingClientRect().top - a.top;
        if (docked && board0) board0.scrollTop += delta;
        else window.scrollBy({ top: delta });
      }
      anchor.current = null;
      captured.current = null;
      wasDocked.current = docked;
    }
    // the newest panel in view when the row scrolls sideways
    if (docked) el.scrollLeft = el.scrollWidth;
    // then keep the source of what just opened where it was, and in view (a ?panels= link: every source)
    // (only once the panels it is about are here: an effect can run again before that)
    const r = panels.length ? reveal.current : null;
    if (r) reveal.current = null;
    if (r && !phone) {
      const all = () => panels.forEach((p, i) => p.source && p.source.column === i && revealIn(el, p.source.column, p.source.num, p.source.via, null, parentOf, p.source.part));
      if ("all" in r) {
        all();
        // cards that just mounted can still change height for a moment (a long text folds behind "Read
        // the full entry" once it can be measured), which moves a source row out of its column's view:
        // while the columns settle, keep every source of a ?panels= link in view, so each bridge is drawn
        settle(el, () => {
          all();
          schedule.current();
        });
      } else revealIn(el, r.column, r.num, r.via, r.top, parentOf, r.part);
    }
    placeBridges(el, state.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idKey, foldKey, docked, phone, avail]);

  // and again whenever anything in the row scrolls, resizes or re-renders
  useLayoutEffect(() => schedule.current());
  useEffect(() => {
    const el = row.current;
    if (!el) return;
    const go = () => schedule.current();
    el.addEventListener("scroll", go, { capture: true, passive: true });
    window.addEventListener("resize", go);
    window.addEventListener("scroll", go, { passive: true });
    const mo = new MutationObserver(go);
    mo.observe(el, { childList: true, subtree: true, characterData: true });
    const ro = new ResizeObserver(go);
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf.current);
      el.removeEventListener("scroll", go, { capture: true });
      window.removeEventListener("resize", go);
      window.removeEventListener("scroll", go);
      mo.disconnect();
      ro.disconnect();
    };
  }, []);

  // "Scroll to it" and the bridge: unfold the source's column if it is a spine, then show and flash it
  const pendingShow = useRef<number | null>(null);
  const showSource = (index: number) => {
    const p = panels[index];
    const s = p?.source;
    const el = row.current;
    if (!s || !el) return;
    const holder = s.column > 0 ? panels[s.column - 1] : null;
    if (holder && folded.has(holder.id)) {
      pendingShow.current = index;
      return onExpand(holder.id);
    }
    const found = locate(el, s.column, s.num, s.via, parentOf, s.part);
    if (!found) return onMissingSource(s.column, s.via ?? s.num);
    if (docked) scrollToShow(el, s.column, found.shape);
    else found.shape.scrollIntoView({ block: "center", behavior: "smooth" });
    found.card.dataset.flash = "1";
    setTimeout(() => delete found.card.dataset.flash, 1500);
  };
  useImperativeHandle(handle, () => ({ showSource }));
  useEffect(() => {
    const i = pendingShow.current;
    if (i == null) return;
    pendingShow.current = null;
    requestAnimationFrame(() => showSource(i));
  });

  return (
    <div className="cols" ref={row} data-docked={docked ? "1" : undefined}>
      <div className="col" data-col="0" data-narrow={docked ? "1" : undefined}>
        {board}
      </div>
      {panels.map((p, i) => {
        const spine = folded.has(p.id);
        return (
          <section
            key={p.id}
            className="pcol"
            data-col={i + 1}
            data-kind={p.kind}
            data-spine={spine ? "1" : undefined}
            data-top={i === panels.length - 1 ? "1" : undefined}
            data-pinned={p.pinned ? "1" : undefined}
            aria-label={p.label}
          >
            <button type="button" className="spine" tabIndex={spine ? 0 : -1} title={`Expand ${p.spine}`} aria-label={`Expand ${p.label}${p.pinned ? " (pinned)" : ""}`} onClick={() => onExpand(p.id)}>
              <span className="n">{i + 1}</span>
              <span className="lbl">{p.spine}</span>
              {p.pinned && (
                <span className="pin" title="Pinned">
                  <Icon name="pin" className="on" />
                </span>
              )}
            </button>
            <div className="pscroll">{p.content}</div>
            {p.source && p.source.column === i && (
              <button
                type="button"
                className="bridge"
                title={`From ${p.source.label}: scroll to it`}
                aria-label={`From ${p.source.label}: scroll to it`}
                onClick={() => showSource(i)}
              >
                <span className="ar up" aria-hidden="true">
                  &uarr;
                </span>
                <span className="bk">{p.source.label}</span>
                <span className="ar down" aria-hidden="true">
                  &darr;
                </span>
              </button>
            )}
          </section>
        );
      })}
    </div>
  );
}
